/**
 * Chat Page - Landlord
 * Conversation list with message thread view
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Send as TelegramLinkedIcon } from 'lucide-react';
import { useTrackFeature } from '@resida/analytics';
import {
  ConversationList,
  Conversation,
  ChatContainer,
  MessageList,
  Message,
  ConversationHeader,
  Avatar,
  TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import '@/styles/chat-kit.css';
import { useAuth } from '../hooks/useAuth';
import { useConversationSubscriptionWithCallbacks, type ReadReceipt } from '../hooks/useCentrifugo';
import { useDomainEvents, type NewChatMessageData } from '../hooks/useDomainEvents';
import {
  listConversations,
  listMessages,
  sendMessage,
  markConversationRead,
  downloadChatAttachment,
  uploadChatAttachment,
  getConversationReadReceipts,
  type ChatConversation,
  type ChatMessage as ApiChatMessage,
  type CreateMessageInput,
  type ChatReadReceipt,
} from '../lib/api';
import { NewConversationDialog } from '../components/chat/NewConversationDialog';
import { TestableMessageInput } from '../components/chat/TestableMessageInput';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { formatUnitLabel } from '@resida/shared';

// Generate avatar URL from display name
function getAvatarUrl(name: string): string {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=3b82f6&color=fff&size=128`;
}

export function ChatPage() {
  useTrackFeature('chat', 'landlord');
  const { manager } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialConversationId = searchParams.get('conversationId');
  // TG-14: Unit Detail Page's "Message this unit's tenants" link hands off here when the
  // unit has no conversation yet — pre-opens the unified New Conversation dialog with the
  // unit already selected. Captured once via the lazy initializer (not re-read on every
  // render) since the param is consumed/cleared right after.
  const [pendingNewConversationUnitId, setPendingNewConversationUnitId] = useState<string | null>(
    () => searchParams.get('newConversationUnitId')
  );
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
    initialConversationId ?? null
  );
  const [messages, setMessages] = useState<ApiChatMessage[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isNewConversationDialogOpen, setIsNewConversationDialogOpen] = useState<boolean>(
    () => !!searchParams.get('newConversationUnitId')
  );
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [otherPartyLastReadMessageId, setOtherPartyLastReadMessageId] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load conversations on mount
  const fetchConversations = useCallback(async () => {
    try {
      setIsLoadingConversations(true);
      setError(null);
      const { conversations: convos } = await listConversations();
      setConversations(convos);
      // Only auto-select if no conversationId was provided via query param
      if (!initialConversationId && convos.length > 0 && !selectedConversationId) {
        // Do not auto-select on landlord — there are multiple conversations
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  }, [initialConversationId, selectedConversationId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Consume `newConversationUnitId` from the URL once (it already drove the initial state
  // above) so it doesn't linger and re-trigger the dialog on a later manual navigation.
  useEffect(() => {
    if (pendingNewConversationUnitId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('newConversationUnitId');
          return next;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle notification click postMessage from service worker
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data.conversationId) {
        setSelectedConversationId(event.data.conversationId);
        setMobileShowChat(true);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  // Load messages when conversation is selected
  const fetchMessages = useCallback(async (conversationId: string, cursor?: string) => {
    try {
      setIsLoadingMessages(true);
      setError(null);
      const result = await listMessages(conversationId, { cursor, limit: 50 });
      console.log('Fetched messages:', result.messages.length, 'messages');
      result.messages.forEach(msg => {
        if (msg.attachments && msg.attachments.length > 0) {
          console.log('Message with attachments:', msg.id, msg.attachments);
        }
      });

      if (cursor) {
        // Append older messages
        setMessages((prev) => [...result.messages, ...prev]);
      } else {
        // Initial load
        setMessages(result.messages);
        // Scroll to bottom on initial load
        setTimeout(() => {
          if (messageListRef.current) {
            messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
          }
        }, 100);

        // Mark conversation as read when messages are loaded
        if (result.messages.length > 0) {
          const lastMessage = result.messages[result.messages.length - 1];
          if (lastMessage) {
            markConversationRead(conversationId, { lastReadMessageId: lastMessage.id })
              .then(() => {
                // BR-15/IAM-BR-RT2 (SAGA-6 step 5) — AppShell's chat nav badge is a SEPARATE
                // React Query cache entry (['chat-conversations-summary']) from this page's own
                // local `conversations` state below; updating local state alone left the nav
                // badge showing the stale count until its 30s staleTime elapsed.
                void queryClient.invalidateQueries({ queryKey: ['chat-conversations-summary'] });
              })
              .catch(console.error);
            // Update local conversations state to clear unread badge
            setConversations((prev) =>
              prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
            );
          }
        }
      }

      setNextCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setIsLoadingMessages(false);
    }
  }, [queryClient]);

  useEffect(() => {
    if (selectedConversationId) {
      fetchMessages(selectedConversationId);
    } else {
      setMessages([]);
      setNextCursor(null);
      setHasMore(false);
    }
  }, [selectedConversationId, fetchMessages]);

  // Fetch initial read receipt state for the selected conversation
  useEffect(() => {
    if (!selectedConversationId || !manager) {
      setOtherPartyLastReadMessageId(null);
      return;
    }
    getConversationReadReceipts(selectedConversationId)
      .then(({ readReceipts }) => {
        // Find the other party's read receipt (not the current manager)
        const otherPartyReceipt = readReceipts.find((r: ChatReadReceipt) => r.identityId !== manager.id);
        setOtherPartyLastReadMessageId(otherPartyReceipt?.lastReadMessageId ?? null);
      })
      .catch(console.error);
  }, [selectedConversationId, manager]);

  // Real-time message subscription
  useConversationSubscriptionWithCallbacks(selectedConversationId, {
    onMessage: useCallback((message: ApiChatMessage) => {
      setMessages((prev) => {
        // Dedup: optimistic update may have already added this message
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
      // Scroll to bottom on new message
      setTimeout(() => {
        if (messageListRef.current) {
          messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
      }, 50);

      // Mark as read if this is not my message
      if (selectedConversationId && manager && message.senderIdentityId !== manager.id) {
        markConversationRead(selectedConversationId, { lastReadMessageId: message.id })
          .then(() => {
            // BR-15/IAM-BR-RT2 — new_chat_message already invalidated AppShell's nav-badge
            // query via useDomainEvents (incrementing it); this corrects it back down now
            // that the message arrived on an already-open, already-read conversation.
            void queryClient.invalidateQueries({ queryKey: ['chat-conversations-summary'] });
          })
          .catch(console.error);
        // Clear the unread badge locally
        setConversations((prev) =>
          prev.map((c) => (c.id === selectedConversationId ? { ...c, unreadCount: 0 } : c))
        );
      }
    }, [selectedConversationId, manager, queryClient]),
    onReadReceipt: useCallback((receipt: ReadReceipt) => {
      // Update the other party's read position if it's not our own receipt
      if (manager && receipt.identityId !== manager.id) {
        setOtherPartyLastReadMessageId(receipt.lastReadMessageId);
      }
    }, [manager]),
  });

  // BR-15/IAM-BR-RT2 — per-conversation unreadCount comes from the server; the event only
  // triggers a re-fetch of the conversation list (never accumulated with prev + 1).
  useDomainEvents({
    new_chat_message: useCallback((data: NewChatMessageData) => {
      if (data.conversationId !== selectedConversationId) {
        void fetchConversations();
      }
    }, [selectedConversationId, fetchConversations]),
  });

  // Handle file attachment
  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !selectedConversationId) return;

    try {
      setIsUploadingAttachments(true);
      setError(null);

      // Upload and send each file immediately as a message
      for (const file of Array.from(files)) {
        const attachment = await uploadChatAttachment(file);

        // Send immediately as a message
        const messageData: CreateMessageInput = {
          content: `📎 ${attachment.fileName}`,
          attachmentIds: [attachment.id],
        };

        await sendMessage(selectedConversationId, messageData);
      }
    } catch (err) {
      console.error('Attachment upload error:', err);
      setError('Failed to upload attachment. Please try again.');
    } finally {
      setIsUploadingAttachments(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [selectedConversationId]);


  // Send message
  const handleSendMessage = useCallback(async (textContent: string) => {
    if (!selectedConversationId || !textContent.trim() || isSending) return;

    try {
      setIsSending(true);
      const messageData: CreateMessageInput = {
        content: textContent.trim(),
      };

      const { message } = await sendMessage(selectedConversationId, messageData);
      // Optimistically add sent message to UI (Centrifugo may also deliver it)
      setMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev;
        return [...prev, message];
      });
      setTimeout(() => {
        if (messageListRef.current) {
          messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
      }, 50);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  }, [selectedConversationId, isSending]);

  // Load more messages (pagination)
  const handleLoadMore = useCallback(() => {
    if (selectedConversationId && hasMore && nextCursor && !isLoadingMessages) {
      fetchMessages(selectedConversationId, nextCursor);
    }
  }, [selectedConversationId, hasMore, nextCursor, isLoadingMessages, fetchMessages]);

  // Select conversation
  const handleConversationSelect = useCallback((conversationId: string) => {
    setSelectedConversationId(conversationId);
    setMobileShowChat(true);
  }, []);

  const handleBackToList = useCallback(() => {
    setMobileShowChat(false);
  }, []);

  const selectedConversation = conversations.find((c) => c.id === selectedConversationId);

  if (isLoadingConversations) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <PageHeader title="Conversations" />
        <div className="flex flex-col gap-3 mt-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex overflow-hidden"
      style={{ height: 'calc(100vh - 60px)' }}
      data-testid="chat-page"
    >
      {/* Conversation list panel — full width on mobile (when not in chat), fixed sidebar on desktop */}
      <div className={`flex-col bg-card border-r border-border w-full md:w-72 md:flex-shrink-0 overflow-y-auto ${mobileShowChat ? 'hidden md:flex' : 'flex'}`}>
        <div className="px-4 py-4 border-b border-border flex justify-between items-center flex-shrink-0">
          <h2 className="m-0 text-xl font-semibold text-foreground">Conversations</h2>
          <Button
            type="button"
            size="sm"
            onClick={() => setIsNewConversationDialogOpen(true)}
            aria-label="Start new conversation"
            data-testid="new-conversation-button"
          >
            New
          </Button>
        </div>

        {error && (
          <div
            className="p-3 m-2 bg-destructive/10 border border-destructive rounded text-sm text-destructive"
            data-testid="error-message"
          >
            {error}
          </div>
        )}

        {conversations.length === 0 ? (
          <div
            className="p-6 text-center text-muted-foreground"
            data-testid="empty-conversations"
          >
            <p>No conversations yet</p>
            <p className="text-sm mt-2">
              Start a new conversation to communicate with tenants
            </p>
          </div>
        ) : (
          <ConversationList>
            {conversations.map((conversation) => {
              const participantNames = conversation.participants
                .filter((p) => p.participantType === 'TENANT')
                .map((p) => p.displayName)
                .join(', ');

              const displayName = participantNames || 'Conversation';
              const info = formatUnitLabel(conversation.unitNumber) ?? (conversation.propertyName || '');

              // List-row Telegram badge (TG-14), alongside the existing detail-header badge
              // below. Trusts the list endpoint's `telegramLinked` as-is here (list-row badge
              // only) — the selected/displayed conversation re-fetches the authoritative value
              // separately (see selectedConversationTelegramLinked above).
              const nameNode = conversation.telegramLinked ? (
                <span className="inline-flex items-center gap-1">
                  {displayName}
                  <TelegramLinkedIcon
                    className="h-3 w-3 text-primary shrink-0"
                    aria-hidden="true"
                    data-testid={`conversation-telegram-badge-${conversation.id}`}
                  />
                  <span className="sr-only">Linked via Telegram</span>
                </span>
              ) : displayName;

              return (
                <Conversation
                  key={conversation.id}
                  name={nameNode}
                  info={info}
                  lastActivityTime={
                    conversation.lastMessage
                      ? new Date(conversation.lastMessage.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : ''
                  }
                  active={selectedConversationId === conversation.id}
                  onClick={() => handleConversationSelect(conversation.id)}
                  data-testid={`conversation-${conversation.id}`}
                  unreadCnt={conversation.unreadCount}
                >
                  {conversation.lastMessage && (
                    <Conversation.Content>
                      {conversation.lastMessage.content}
                    </Conversation.Content>
                  )}
                </Conversation>
              );
            })}
          </ConversationList>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,application/pdf,.doc,.docx,.txt"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Chat/message panel — hidden on mobile until conversation selected, always shown on desktop */}
      <div className={`flex-col flex-1 min-w-0 overflow-hidden ${mobileShowChat ? 'flex' : 'hidden md:flex'}`}>
        {/* Mobile back button */}
        <div className="flex md:hidden items-center h-12 px-4 border-b border-border bg-card flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackToList}
            aria-label="Back to conversations"
            className="text-primary"
          >
            ← Conversations
          </Button>
        </div>

        {selectedConversation ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', overflow: 'hidden' }}>
            {error && (
              <div
                className="p-2 m-2 bg-destructive/10 border border-destructive rounded text-sm text-destructive"
                data-testid="chat-error-message"
              >
                {error}
              </div>
            )}

            <ChatContainer style={{ flex: 1, minHeight: 0, width: '100%' }}>
              <ConversationHeader>
                <Avatar
                  name={
                    selectedConversation.participants
                      .filter((p) => p.participantType === 'TENANT')
                      .map((p) => p.displayName)
                      .join(', ') || 'Conversation'
                  }
                  src={getAvatarUrl(
                    selectedConversation.participants
                      .filter((p) => p.participantType === 'TENANT')
                      .map((p) => p.displayName)
                      .join(', ') || 'Conversation'
                  )}
                />
                <ConversationHeader.Content
                  userName={
                    selectedConversation.participants
                      .filter((p) => p.participantType === 'TENANT')
                      .map((p) => p.displayName)
                      .join(', ') || 'Conversation'
                  }
                  info={
                    formatUnitLabel(selectedConversation.unitNumber) ?? (selectedConversation.propertyName || '')
                  }
                />
                {selectedConversation.telegramLinked && (
                  <ConversationHeader.Actions>
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary shrink-0"
                      data-testid="telegram-linked-badge"
                    >
                      <TelegramLinkedIcon className="h-3 w-3" aria-hidden="true" />
                      Linked via Telegram
                    </span>
                  </ConversationHeader.Actions>
                )}
              </ConversationHeader>

              <MessageList
                typingIndicator={isSending ? <TypingIndicator content="Sending..." /> : undefined}
                loadingMore={isLoadingMessages}
                ref={messageListRef as unknown as React.Ref<HTMLDivElement>}
                onYReachStart={handleLoadMore}
                data-testid="message-list"
              >
                {(() => {
                  const lastReadIndex = otherPartyLastReadMessageId
                    ? messages.findIndex((m) => m.id === otherPartyLastReadMessageId)
                    : -1;

                  return messages.map((message, index) => {
                    const isOwnMessage = manager && message.senderIdentityId === manager.id;

                    if (message.attachments && message.attachments.length > 0) {
                      console.log('Message has attachments:', message.id, message.attachments);
                    }

                    const showReadReceipt =
                      isOwnMessage &&
                      lastReadIndex >= 0 &&
                      index === lastReadIndex;

                    return (
                      <Message
                        key={message.id}
                        model={{
                          message: message.content,
                          sentTime: new Date(message.createdAt).toISOString(),
                          sender: message.senderDisplayName,
                          direction: isOwnMessage ? 'outgoing' : 'incoming',
                          position: 'normal',
                        }}
                        data-testid={`message-${message.id}`}
                      >
                        <Avatar name={message.senderDisplayName} src={getAvatarUrl(message.senderDisplayName)} />
                        {message.attachments && message.attachments.length > 0 && (
                          <Message.Footer>
                            {message.attachments.map((attachment) => (
                              <div key={attachment.id} className="mt-2">
                                <a
                                  href="#"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    try {
                                      const response = await downloadChatAttachment(attachment.id);
                                      const blob = await response.blob();
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url;
                                      a.download = attachment.fileName;
                                      a.click();
                                      URL.revokeObjectURL(url);
                                    } catch (err) {
                                      console.error('Failed to download attachment:', err);
                                    }
                                  }}
                                  className="text-primary underline"
                                  data-testid={`attachment-${attachment.id}`}
                                >
                                  📎 {attachment.fileName}
                                </a>
                              </div>
                            ))}
                          </Message.Footer>
                        )}
                        {showReadReceipt && (
                          <Message.Footer>
                            <span
                              className="text-[0.7rem] text-muted-foreground flex justify-end"
                              data-testid={`read-receipt-${message.id}`}
                            >
                              ✓ Read
                            </span>
                          </Message.Footer>
                        )}
                      </Message>
                    );
                  });
                })()}
              </MessageList>
            </ChatContainer>

            <TestableMessageInput
              placeholder={isUploadingAttachments ? "Uploading attachment..." : "Type a message..."}
              onSend={handleSendMessage}
              disabled={isSending || isUploadingAttachments}
              attachButton={true}
              onAttachClick={handleAttachClick}
              data-testid="message-input"
            />
          </div>
        ) : (
          <div
            className="flex flex-col flex-1 items-center justify-center h-full text-muted-foreground"
            data-testid="no-conversation-selected"
          >
            <h3>No conversation selected</h3>
            <p>Choose a conversation from the list or start a new one</p>
          </div>
        )}
      </div>

      <NewConversationDialog
        isOpen={isNewConversationDialogOpen}
        initialUnitId={pendingNewConversationUnitId ?? undefined}
        onClose={() => {
          setIsNewConversationDialogOpen(false);
          setPendingNewConversationUnitId(null);
        }}
        onConversationCreated={(conversationId) => {
          fetchConversations();
          setSelectedConversationId(conversationId);
          setMobileShowChat(true);
        }}
      />
    </div>
  );
}
